import { RefObject } from 'preact';
import { useCallback, useRef } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { Props as ListViewProps } from '../UNSAFE_ListView';
import { getFirstVisibleKey, keyExtractor } from '../utils/PRIVATE_collectionUtils';
import { isKeyDefined } from '../utils/UNSAFE_keys';
import { ITEM_SELECTOR } from './ListItem';
import { useTabbableModeSet } from '../hooks/PRIVATE_useTabbableModeSet';
import { findElementByKey } from '../utils/PRIVATE_collectionUtils';
import { scrollToVisible } from './ListUtils';

export const useFocusTabbableMode = <K extends string | number, D>(
  currentKey: ListViewProps<K, D>['currentKey'],
  onCurrentKeyChange: ListViewProps<K, D>['onCurrentKeyChange'],
  allowTabbableMode: ListViewProps<K, D>['allowTabbableMode'],
  rootRef: RefObject<HTMLDivElement>,
  viewportConfig: ListViewProps<K, D>['viewportConfig'],
  scrollToVisibleOffset: ListViewProps<K, D>['scrollToVisibleOffset']
) => {
  const pendingCurrentKey = useRef<K>();

  const [isCurrentTabbableKey, tabbableModeProps] = useTabbableModeSet(
    rootRef,
    (element) => {
      // we don't want List to go into Tabbable mode when user clicks on expander
      return isExpander(element) ? null : (keyExtractor(element, ITEM_SELECTOR) as K);
    },
    currentKey,
    onCurrentKeyChange
  );

  const handleInitialFocus = useCallback(() => {
    if (
      rootRef.current &&
      onCurrentKeyChange &&
      (!allowTabbableMode || isCurrentTabbableKey(undefined))
    ) {
      const firstKey =
        pendingCurrentKey.current || (getFirstVisibleKey(rootRef.current, ITEM_SELECTOR) as K);
      if (isKeyDefined(firstKey)) {
        onCurrentKeyChange({ value: firstKey });
      }
    }
  }, [rootRef, onCurrentKeyChange, allowTabbableMode, isCurrentTabbableKey]);

  const handleFocus = useCallback(
    (event: FocusEvent) => {
      if (isExpander(event.target as HTMLElement)) {
        rootRef.current?.focus();
        const key = keyExtractor(event.target as HTMLElement, ITEM_SELECTOR);
        if (onCurrentKeyChange && isKeyDefined(key)) {
          onCurrentKeyChange({ value: key as K });
        }
        return;
      }
      if (!isKeyDefined(currentKey)) {
        onCurrentKeyChange && handleInitialFocus();
      } else if (rootRef.current) {
        // if currentKey is defined, make sure scroll the current item into view when listview regains focus
        const elem = findElementByKey(rootRef.current, currentKey as K, ITEM_SELECTOR);
        if (elem) {
          const scroller = viewportConfig?.scroller();
          const offset = scrollToVisibleOffset ? scrollToVisibleOffset(rootRef.current) : 0;
          scrollToVisible(elem, scroller != null ? scroller : rootRef.current, offset);
        }
      }
    },
    [currentKey, handleInitialFocus, onCurrentKeyChange, rootRef]
  );

  /**
   * Need to track pointer down element to set currentItem when initial focus happens
   */
  const handlePointerDown = useCallback((event: PointerEvent) => {
    const key = keyExtractor(event.target as HTMLElement, ITEM_SELECTOR);
    if (isKeyDefined(key)) {
      pendingCurrentKey.current = key as K;
    }
  }, []);

  return {
    eventProps: mergeProps(allowTabbableMode ? tabbableModeProps : {}, {
      onFocus: handleFocus,
      onPointerDown: handlePointerDown
    }),
    isCurrentTabbableKey
  };
};

const isExpander = (elem: HTMLElement) => {
  if (elem.parentElement && elem.parentElement.classList.contains('oj-listview-expander')) {
    return true;
  }
  return false;
};
